import { omit } from "lodash";
import { getFromDataPath, mutateByPath } from "../../utility";
import { isWhereClauseWithCompoundExpression, isWhereClauseWithCondition } from "../typeGuards";
import { normalizeSubClauseLevelsAndOrders } from "../utility/parseWhereClauseFromCode";
import { ARSContextState } from "./interface";

/**
 * Removes a where clause from a compound expression and collapses if necessary.
 * Handles both SubClause and WhereClause compound expressions.
 *
 * @param state - The current ARSContextState
 * @param dataPath - The path to the entity containing the compound expression
 * @param id - The ID of the clause to remove
 * @returns The updated ARSContextState
 */
export const removeWhereClause = (
  state: ARSContextState,
  dataPath: string,
  id: string,
): ARSContextState => {
  const whereClause = getFromDataPath(dataPath, state, isWhereClauseWithCompoundExpression);
  if (whereClause) {
    const ce = whereClause.compoundExpression;
    if (!ce.whereClauses || ce.whereClauses.length <= 1) {
      throw new Error(
        `Cannot remove where clause from ${dataPath} because it does not have multiple clauses`,
      );
    } else if (ce.whereClauses.length === 2) {
      // If only 2 clauses, remove the compound expression and revert to single condition
      const remainingClause = ce.whereClauses.find((clause) => clause.id !== id);
      if (!remainingClause) {
        // It may have already been removed
        return state;
      } else if (isWhereClauseWithCondition(remainingClause)) {
        const newEntity = {
          ...omit(whereClause, "compoundExpression"),
          condition: remainingClause.condition,
          level: remainingClause.level - 1,
          order: 0,
        };
        return mutateByPath({ ...state, changed: true }, dataPath, newEntity);
      } else if (isWhereClauseWithCompoundExpression(remainingClause)) {
        // Check if parent entity only has one SubClause - if so, promote compoundExpression up
        const parentPath = dataPath.substring(
          0,
          dataPath.lastIndexOf("/compoundExpression/whereClauses"),
        );
        const parentEntity = getFromDataPath(
          parentPath,
          state,
          isWhereClauseWithCompoundExpression,
        );
        if (parentEntity && parentEntity.compoundExpression.whereClauses?.length === 1) {
          // Promote the compound expression to replace the parent's compoundExpression
          const normalizedCompound = {
            ...remainingClause.compoundExpression,
            whereClauses: remainingClause.compoundExpression.whereClauses?.map((clause) => ({
              ...clause,
            })),
          };
          // Normalize levels based on parent entity level
          normalizedCompound.whereClauses?.forEach((clause, index) => {
            normalizeSubClauseLevelsAndOrders(clause, parentEntity.level + 1, index);
          });
          return mutateByPath(
            { ...state, changed: true },
            `${parentPath}/compoundExpression`,
            normalizedCompound,
          );
        } else {
          // Multiple SubClauses in parent, just update this one
          const normalizedCompound = {
            ...remainingClause.compoundExpression,
            whereClauses: remainingClause.compoundExpression.whereClauses?.map((clause) => ({
              ...clause,
            })),
          };
          normalizedCompound.whereClauses?.forEach((clause, index) => {
            normalizeSubClauseLevelsAndOrders(clause, whereClause.level + 1, index);
          });
          const newEntity = {
            ...whereClause,
            compoundExpression: normalizedCompound,
            level: whereClause.level,
            order: whereClause.order,
          };
          return mutateByPath({ ...state, changed: true }, dataPath, newEntity);
        }
      } else if ("subClauseId" in whereClause) {
        const newEntity = {
          ...omit(whereClause, "compoundExpression"),
          subClauseId: remainingClause.subClauseId,
          level: remainingClause.level - 1,
          order: whereClause.order,
        };
        return mutateByPath({ ...state, changed: true }, dataPath, newEntity);
      } else {
        // Shouldn't be left with a reference on a whereClause
        throw new Error(
          `Cannot collapse to single clause at ${dataPath} - remaining clause is a reference`,
        );
      }
    } else {
      // More than 2 clauses, just remove the specified clause
      const newWhereClauses = ce.whereClauses.filter((clause) => clause.id !== id);
      const newCompoundExpression = {
        ...ce,
        whereClauses: newWhereClauses,
      };
      return mutateByPath(
        { ...state, changed: true },
        `${dataPath}/compoundExpression`,
        newCompoundExpression,
      );
    }
  } // If we can't find a compound expression it may have already been removed
  else return state;
};
